import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NetworkStatus, useQuery, useLazyQuery } from '@apollo/client';
import DateRangeFilter from '../../components/DateRangeFilter';
import { debounce } from 'lodash';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as XLSX from 'xlsx';
import { GET_ALL_PAYMENTS, GET_PENDING_PAYMENTS } from './queries';
import {
  PAYMENT_COLUMNS,
  PENDING_COLUMNS,
  fmtMoney,
  type Column,
  type Payment,
  type PendingPayment,
} from './columns';
import { fmtAgoEpoch } from '../PdCases/format';
import RechargeTypeTag from '../../components/RechargeTypeTag';

const RED = '#E60012';

/**
 * How often the page re-reads itself.
 *
 * A payment lands with nobody touching this page: HDFC posts the alert to
 * oem_tool_transactions, which writes transactions_history and settles the
 * session here. Until now none of it appeared without a reload, so the people
 * watching for a customer's money had to sit on F5. 30s matches the PD board
 * and is cheap -- one paginated query.
 */
const POLL_MS = 30000;

/** Suspended while the tab is hidden; the visibility listener below re-reads
 *  the moment it comes back, so returning never shows a stale screen. */
const pausedWhenHidden = () => document.visibilityState === 'hidden';

/** A row that arrived by a background read rather than one the user asked for. */
const FRESH_ROW_SX = { backgroundColor: '#E8F5E9' };

const headerCellSx = {
  backgroundColor: RED,
  color: '#fff',
  fontWeight: 700,
  fontSize: { xs: '0.72rem', sm: '0.8rem' },
  whiteSpace: 'nowrap',
  padding: { xs: '8px', sm: '10px 12px' },
  borderRight: '1px solid rgba(255,255,255,0.18)',
  position: 'sticky' as const,
  top: 0,
  zIndex: 11,
};

const cellSx = {
  padding: { xs: '6px 8px', sm: '8px 12px' },
  fontSize: { xs: '0.74rem', sm: '0.8rem' },
  borderRight: '1px solid #eee',
  verticalAlign: 'top' as const,
};

/**
 * One table body, shared by both tabs.
 *
 * Both tabs render from the same column definitions, so a column added in
 * columns.ts appears in the table and the export together rather than in
 * whichever one someone remembered to update.
 */
function DataTable<T>({
  columns,
  rows,
  loading,
  emptyText,
  startIndex,
  rowKey,
  rowSx,
}: {
  columns: Column<T>[];
  rows: T[];
  loading: boolean;
  emptyText: string;
  startIndex: number;
  rowKey: (row: T, i: number) => string;
  rowSx?: (row: T, key: string) => object;
}) {
  const totalCols = columns.length + 1;
  return (
    <TableContainer
      component={Paper}
      sx={{ flex: 1, minHeight: 0, overflow: 'auto', boxShadow: 'none', borderRadius: 0 }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell align="center" sx={{ ...headerCellSx, minWidth: 64 }}>
              Sr.No
            </TableCell>
            {columns.map((c) => (
              <TableCell
                key={c.label}
                align="center"
                sx={{ ...headerCellSx, minWidth: c.width ?? 140 }}
              >
                {c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={totalCols} align="center" sx={{ py: 8, border: 0 }}>
                <CircularProgress size={28} sx={{ color: RED }} />
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={totalCols} align="center" sx={{ py: 8, color: '#888', border: 0 }}>
                <Stack alignItems="center" spacing={1}>
                  <CreditCardIcon sx={{ fontSize: '2.5rem', color: '#ccc' }} />
                  <Typography variant="body2" color="text.secondary">
                    {emptyText}
                  </Typography>
                </Stack>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, idx) => (
              <TableRow
                key={rowKey(row, idx)}
                hover
                sx={{
                  backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa',
                  ...(rowSx ? rowSx(row, rowKey(row, idx)) : {}),
                }}
              >
                <TableCell align="center" sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                  {startIndex + idx + 1}
                </TableCell>
                {columns.map((c) => {
                  const raw = c.value(row);
                  const text =
                    c.money && typeof raw === 'number'
                      ? fmtMoney(raw)
                      : raw === null || raw === undefined || raw === ''
                        ? '—'
                        : String(raw);
                  const isBlank = text === '—';
                  return (
                    <TableCell
                      key={c.label}
                      align={c.align ?? 'left'}
                      sx={{
                        ...cellSx,
                        minWidth: c.width ?? 140,
                        maxWidth: c.wrap ? c.width ?? 240 : undefined,
                        whiteSpace: c.wrap ? 'normal' : 'nowrap',
                        color: isBlank ? '#bbb' : undefined,
                        fontWeight: c.money ? 600 : undefined,
                      }}
                    >
                      {c.tag ? (
                        <RechargeTypeTag
                          value={raw === null || raw === undefined ? null : String(raw)}
                        />
                      ) : c.wrap ? (
                        text
                      ) : (
                        <Tooltip title={text.length > 24 ? text : ''} placement="top" arrow>
                          <Box
                            component="span"
                            sx={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {text}
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export const PaymentsDashboard: React.FC = () => {
  const [tab, setTab] = useState<'PAID' | 'PENDING'>('PAID');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  // Date bounds on the payment date. The PAID/PENDING tabs already are the
  // status filter here, so no status dropdown is mounted beside them.
  //
  // On the PAID tab this filters the bank credit's transaction_date, which is a
  // naive IST timestamp; on PENDING it filters kyc_received_date. Both are
  // handled server-side by utils/dateFilter.ts -- see the note there about the
  // 3h30m skew, which is exactly what this page would have suffered.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [hideTests, setHideTests] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value);
        setPage(0);
      }, 500),
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    debouncedSearch(e.target.value);
  };

  const switchTab = (next: 'PAID' | 'PENDING') => {
    setTab(next);
    setPage(0);
  };

  const variables = {
    limit: rowsPerPage,
    offset: page * rowsPerPage,
    searchTerm: searchTerm || null,
    fromDate: fromDate || null,
    toDate: toDate || null,
  };

  const paidQuery = useQuery(GET_ALL_PAYMENTS, {
    variables,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: tab !== 'PAID',
    pollInterval: POLL_MS,
    skipPollAttempt: pausedWhenHidden,
  });
  const pendingQuery = useQuery(GET_PENDING_PAYMENTS, {
    variables,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: tab !== 'PENDING',
    pollInterval: POLL_MS,
    skipPollAttempt: pausedWhenHidden,
  });

  const [fetchAllPaid] = useLazyQuery(GET_ALL_PAYMENTS);
  const [fetchAllPending] = useLazyQuery(GET_PENDING_PAYMENTS);

  const active = tab === 'PAID' ? paidQuery : pendingQuery;
  const response =
    tab === 'PAID' ? active.data?.getAllPayments : active.data?.getPendingPayments;
  const totalCount: number = response?.count ?? 0;

  // A background read (poll, or the Updated button) keeps the rows on screen.
  // notifyOnNetworkStatusChange is on, so without this every 30s poll would
  // blank the table to a spinner and lose the reader's place.
  const backgroundRead =
    active.networkStatus === NetworkStatus.poll || active.networkStatus === NetworkStatus.refetch;
  const tableLoading = active.loading && !(backgroundRead && response);

  // When the visible data was last read, for the "Updated N min ago" button.
  const [lastLoadedAt, setLastLoadedAt] = useState(() => Math.floor(Date.now() / 1000));
  // Re-render once a minute so that caption ages without a read.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 60000);
    return () => window.clearInterval(id);
  }, []);

  /**
   * Rows that turned up in a background read are tinted green until the view
   * changes, because "the page refreshed" is no use if nobody can see what
   * the refresh brought in. Keys seen on this view are tracked in a ref; a
   * new view (tab, page, search, dates) resets it, so paging never lights up
   * rows that were merely off-screen.
   */
  const viewKey = JSON.stringify([tab, variables]);
  const seenRef = useRef<{ view: string; keys: Set<string> }>({ view: '', keys: new Set() });
  const [freshKeys, setFreshKeys] = useState<Set<string>>(new Set());
  const rowKeyFor = (r: Payment | PendingPayment, i: number): string =>
    tab === 'PAID'
      ? (r as Payment).bankAlertRef || (r as Payment).transactionId || String(i)
      : (r as PendingPayment).sessionId || String(i);

  useEffect(() => {
    if (active.loading || !response) return;
    setLastLoadedAt(Math.floor(Date.now() / 1000));
    const keys: string[] = (response.data ?? []).map(rowKeyFor);
    const seen = seenRef.current;
    if (seen.view !== viewKey) {
      seenRef.current = { view: viewKey, keys: new Set(keys) };
      setFreshKeys(new Set());
      return;
    }
    // Only the first page: both lists are newest-first, so on a later page a
    // new arrival just shifts an older row in from the page before, which is
    // not news.
    const arrived = page === 0 ? keys.filter((k) => !seen.keys.has(k)) : [];
    keys.forEach((k) => seen.keys.add(k));
    if (arrived.length) {
      setFreshKeys((prev) => {
        const next = new Set(prev);
        arrived.forEach((k) => next.add(k));
        return next;
      });
    }
    // rowKeyFor and viewKey are derived from tab/variables, which change
    // together with response; listing them would not change when this runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response, active.loading]);

  /**
   * Coming back to the tab reads the page immediately -- polling is suspended
   * while hidden. Called through a ref so the listener is attached once
   * instead of per render.
   */
  const refetchRef = useRef(active.refetch);
  refetchRef.current = active.refetch;
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void refetchRef.current();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const paidRows: Payment[] = tab === 'PAID' ? (response?.data ?? []) : [];
  const pendingRows: PendingPayment[] = tab === 'PENDING' ? (response?.data ?? []) : [];

  // A recharge is never Rs 1, so these are gateway pokes rather than sales.
  // Hidden by default but filtered client-side only, so the record count still
  // reflects what the bank actually sent and the two never silently disagree.
  const visiblePaid = hideTests ? paidRows.filter((r) => !r.isTestPayment) : paidRows;
  const hiddenTests = paidRows.length - visiblePaid.length;

  const handleExport = async () => {
    if (totalCount === 0) return;
    try {
      setIsExporting(true);
      const exportVars = { ...variables, limit: totalCount, offset: 0 };
      if (tab === 'PAID') {
        const { data } = await fetchAllPaid({ variables: exportVars, fetchPolicy: 'network-only' });
        const all: Payment[] = data?.getAllPayments?.data ?? [];
        const rows = hideTests ? all.filter((r) => !r.isTestPayment) : all;
        writeSheet(PAYMENT_COLUMNS, rows, 'Payments', 'payments');
      } else {
        const { data } = await fetchAllPending({ variables: exportVars, fetchPolicy: 'network-only' });
        const all: PendingPayment[] = data?.getPendingPayments?.data ?? [];
        writeSheet(PENDING_COLUMNS, all, 'Awaiting Payment', 'awaiting-payment');
      }
    } catch (err) {
      console.error('Failed to export:', err);
    } finally {
      setIsExporting(false);
    }
  };

  /** Money stays a number so Excel can sum it; only the table formats it. */
  function writeSheet<T>(columns: Column<T>[], rows: T[], sheetName: string, filePrefix: string) {
    const formatted = rows.map((row, index) => {
      const out: Record<string, string | number | null> = { 'Sr.No': index + 1 };
      for (const c of columns) {
        const v = c.value(row);
        out[c.label] = v === undefined ? '' : v;
      }
      return out;
    });
    const worksheet = XLSX.utils.json_to_sheet(formatted, {
      header: ['Sr.No', ...columns.map((c) => c.label)],
    });
    worksheet['!cols'] = [{ wch: 6 }, ...columns.map((c) => ({ wch: Math.round((c.width ?? 140) / 8) }))];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, `${filePrefix}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Page header */}
      <Box display="flex" alignItems="flex-start" gap={1.5} sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: '#FDECEC',
            flexShrink: 0,
          }}
        >
          <CreditCardIcon sx={{ color: RED, fontSize: '1.6rem' }} />
        </Box>
        <Box>
          <Typography variant="h5" fontSize={{ xs: '1.4rem', sm: '1.9rem' }} fontWeight={700}>
            Payments &amp; Transactions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Every customer payment, with the columns the invoice ledger carries.
          </Typography>
        </Box>
      </Box>

      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 2,
          borderRadius: 3,
          bgcolor: '#fff',
          height: 'calc(100vh - 170px)',
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => switchTab(v)}
          sx={{
            flexShrink: 0,
            borderBottom: '1px solid #eee',
            px: 1,
            '& .MuiTab-root': { fontWeight: 700, textTransform: 'none' },
            '& .Mui-selected': { color: `${RED} !important` },
            '& .MuiTabs-indicator': { backgroundColor: RED },
          }}
        >
          <Tab value="PAID" label="Payments" icon={<CreditCardIcon fontSize="small" />} iconPosition="start" />
          <Tab
            value="PENDING"
            label="Awaiting Payment"
            icon={<HourglassEmptyIcon fontSize="small" />}
            iconPosition="start"
          />
        </Tabs>

        {/* Toolbar */}
        <Box
          sx={{
            flexShrink: 0,
            px: 2,
            pt: 2,
            pb: 1.5,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            flexWrap: 'wrap',
            gap: 1.5,
            rowGap: 2,
            alignItems: { md: 'center' },
            borderBottom: '1px solid #eee',
          }}
        >
          <TextField
            size="small"
            placeholder={
              tab === 'PAID'
                ? 'Search VIN, customer, dealer, session, cheque ref, IMEI...'
                : 'Search VIN, customer, dealer, session...'
            }
            value={searchInput}
            onChange={handleSearchChange}
            sx={{ flex: 1, minWidth: { xs: '100%', md: 300 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {/* Date-only: the PAID / PENDING tabs are the status filter here.
              The label changes with the tab because the two halves filter
              different columns -- a settled credit's bank date vs the date the
              customer's KYC session was raised. */}
          <DateRangeFilter
            label={tab === 'PAID' ? 'Paid' : 'Raised'}
            from={fromDate}
            to={toDate}
            onFromChange={(v) => { setFromDate(v); setPage(0); }}
            onToChange={(v) => { setToDate(v); setPage(0); }}
          />

          {tab === 'PAID' && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={hideTests}
                  onChange={(e) => setHideTests(e.target.checked)}
                  sx={{ '& .Mui-checked': { color: RED }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: RED } }}
                />
              }
              label={
                <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                  Hide ₹1 test payments{hiddenTests ? ` (${hiddenTests} on this page)` : ''}
                </Typography>
              }
            />
          )}
          <Stack direction="row" alignItems="center" spacing={1}>
            {/* Says the page is reading itself, so nobody sits on F5. */}
            <Tooltip
              arrow
              placement="top"
              title={`This page re-reads itself every ${POLL_MS / 1000}s (paused while the tab is hidden). New rows are tinted green. Click to read it now.`}
            >
              <Button
                size="small"
                onClick={() => { void active.refetch(); }}
                startIcon={
                  active.loading
                    ? <CircularProgress size={12} sx={{ color: '#2e7d32' }} />
                    : <RefreshIcon sx={{ fontSize: 16 }} />
                }
                sx={{ textTransform: 'none', fontSize: '0.72rem', color: '#666', whiteSpace: 'nowrap', minWidth: 'auto' }}
              >
                {active.loading ? 'Updating…' : `Updated ${fmtAgoEpoch(lastLoadedAt)}`}
              </Button>
            </Tooltip>
            <Chip
              label={`${totalCount} record${totalCount === 1 ? '' : 's'}`}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
            <Tooltip title="Export to Excel" placement="top" arrow>
              <span>
                <IconButton
                  onClick={handleExport}
                  disabled={isExporting || totalCount === 0}
                  sx={{ color: RED }}
                >
                  {isExporting ? <CircularProgress size={22} sx={{ color: RED }} /> : <CloudDownloadIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>

        {active.error && (
          <Box sx={{ px: 2, pt: 1.5 }}>
            <Alert severity="error" variant="outlined">
              Error loading {tab === 'PAID' ? 'payments' : 'pending sessions'}: {active.error.message}
            </Alert>
          </Box>
        )}
        {response && response.success === false && response.error && (
          <Box sx={{ px: 2, pt: 1.5 }}>
            <Alert severity="error" variant="outlined">
              {response.error}
            </Alert>
          </Box>
        )}

        {tab === 'PAID' ? (
          <DataTable
            columns={PAYMENT_COLUMNS}
            rows={visiblePaid}
            loading={tableLoading}
            emptyText="No matching payments found."
            startIndex={page * rowsPerPage}
            rowKey={rowKeyFor}
            // A test poke that has been un-hidden should look unlike a sale.
            rowSx={(r, key) =>
              r.isTestPayment ? { backgroundColor: '#FFF8E1' } : freshKeys.has(key) ? FRESH_ROW_SX : {}
            }
          />
        ) : (
          <DataTable
            columns={PENDING_COLUMNS}
            rows={pendingRows}
            loading={tableLoading}
            emptyText="Nothing is awaiting payment."
            startIndex={page * rowsPerPage}
            rowKey={rowKeyFor}
            rowSx={(_, key) => (freshKeys.has(key) ? FRESH_ROW_SX : {})}
          />
        )}

        <Box sx={{ flexShrink: 0, borderTop: '1px solid #e0e0e0', overflowX: 'auto' }}>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Box>
      </Card>
    </Box>
  );
};
